import React, { useCallback, useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import InventoryIcon from '@mui/icons-material/Inventory';
import WarningIcon from '@mui/icons-material/Warning';
import BasicCli from '../../main/components/basicCli/BasicCli';
import DeleteModal from '../../main/components/modal/delete/DeleteModal';
import EditModal from '../../main/components/modal/edit/EditModal';
import GeneralTable from '../../main/components/grid/GeneralTable';
import eFormType from '../../main/assets/enums/eFormType';
import { ApiConstants } from '../../main/constants/ApiConstants';
import axiosInstance from '../../main/initializers/axios';
import IGridItems from '../../main/interfaces/IGridItems';
import IPagination from '../../main/interfaces/IPagination';
import IProduct from '../../main/interfaces/IProduct';
import IDailyInventory from '../../main/interfaces/IDailyInventory';
import DailyInventoryForm from './DailyInventoryForm';
import './DailyInventoryGrid.css';
import i18n from 'i18next';

const DAILY_INVENTORY_UPDATED_EVENT = 'dailyInventoryUpdatedAt';

const DailyInventoryGrid = () => {
  const [items, setItems] = useState<IDailyInventory[]>([]);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [selectedVal, setSelectedVal] = useState<IDailyInventory | null>(null);
  const [gridItems, setGridItems] = useState<IGridItems[]>([]);
  const [approved, setApproved] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalEditOpen, setIsModalEditOpen] = useState(false);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [pagination, setPagination] = useState<IPagination>({
    count: 1,
    page: 1,
    itemPerPage: 50,
    itemsPerPage: [10, 20, 50, 100],
  });

  const getProducts = useCallback(() => {
    axiosInstance.get(ApiConstants.Products.list).then((res) => setProducts(res.data ?? []));
  }, []);

  const getTable = useCallback(
    (pag?: IPagination, gridOverride?: IGridItems[]) => {
      const effectivePagination = pag ?? pagination;
      const effectiveGrid = gridOverride ?? gridItems ?? [];

      axiosInstance.post(ApiConstants.dailyInventory.listCt, { gridItems: effectiveGrid, pagination: effectivePagination }).then((res) => {
        const p = res.data?.pagination ?? {};
        setItems(res.data?.items ?? []);
        setGridItems(res.data?.gridItems ?? []);
        setPagination((prev) => ({
          ...prev,
          count: p.count ?? prev.count,
          page: p.page ?? prev.page,
          itemPerPage: p.itemPerPage ?? prev.itemPerPage,
          itemsPerPage: p.itemsPerPage ?? prev.itemsPerPage,
        }));
      });
    },
    [gridItems, pagination],
  );

  const stats = useMemo(() => {
    const total = items.length;
    const low = items.filter((item) => Number(item.quantity ?? 0) <= Number(item.warningQuantity ?? 0)).length;
    const zero = items.filter((item) => Number(item.quantity ?? 0) <= 0).length;
    return { total, low, zero };
  }, [items]);

  const deleteDataSet = (id: number) => {
    axiosInstance.delete(ApiConstants.dailyInventory.delete(id)).then(() => {
      setIsModalDeleteOpen(false);
      setItems((prev) => prev.filter((x) => x.id !== id));
    });
  };

  useEffect(() => {
    getTable();
    getProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleDailyInventoryUpdate = (event: StorageEvent) => {
      if (event.key === DAILY_INVENTORY_UPDATED_EVENT) {
        getTable();
      }
    };

    window.addEventListener('storage', handleDailyInventoryUpdate);
    return () => window.removeEventListener('storage', handleDailyInventoryUpdate);
  }, [getTable]);

  return (
    <BasicCli>
      <div className="diWrap bcPageContent">
        <div className="diContainer">
          <div className="bcPageHeader">
            <h1 className="bcPageTitle">{i18n.t('dailyInventory.title', { defaultValue: 'Daily Inventory' })}</h1>
            <div className="bcPageHeaderActions">
              <button className="ui-control ui-btn ui-btn--primary diAddBtn" onClick={() => setIsModalOpen(true)}>
                <AddIcon />
                <span>{i18n.t('dailyInventory.actions.add', { defaultValue: 'Add daily inventory' })}</span>
              </button>
            </div>
          </div>

          <div className="diCards">
            <div className="bcCard diCard">
              <InventoryIcon className="diCardIcon" />
              <span>{i18n.t('dailyInventory.stats.totalRows', { defaultValue: 'Total rows' })}</span>
              <strong>{stats.total}</strong>
            </div>
            <div className="bcCard diCard">
              <WarningIcon className="diCardIcon diCardIcon--warn" />
              <span>{i18n.t('dailyInventory.stats.warning', { defaultValue: 'Warning' })}</span>
              <strong>{stats.low}</strong>
            </div>
            <div className="bcCard diCard">
              <WarningIcon className="diCardIcon diCardIcon--danger" />
              <span>{i18n.t('dailyInventory.stats.out', { defaultValue: 'Out' })}</span>
              <strong>{stats.zero}</strong>
            </div>
          </div>

          <div className="diTable">
            <GeneralTable
              data={items}
              columns={gridItems}
              gridEntity={25}
              title=""
              hasRp={true}
              selectedItem={(val: any) => setSelectedVal(val)}
              onEdit={(item: IDailyInventory) => {
                setSelectedVal(item);
                setIsModalEditOpen(true);
              }}
              onDelete={(item: IDailyInventory) => {
                setSelectedVal(item);
                setIsModalDeleteOpen(true);
              }}
              pagination={pagination}
              selectedItemPagination={(val: any) => {
                setPagination(val);
                getTable(val);
              }}
              onFilterClose={(nextGridItems) => {
                setGridItems(nextGridItems);
                getTable(undefined, nextGridItems);
              }}
            />
          </div>

          {isModalDeleteOpen && (
            <DeleteModal width="500px" handleClose={() => setIsModalDeleteOpen(false)} handleDelete={() => deleteDataSet(selectedVal?.id ?? 0)}>
              {i18n.t('dailyInventory.deleteDialog.message', {
                defaultValue: 'Delete daily inventory row #{{id}}?',
                id: selectedVal?.id,
              })}
            </DeleteModal>
          )}

          {isModalEditOpen && (
            <EditModal title={i18n.t('dailyInventory.actions.edit', { defaultValue: 'Edit Daily Inventory' })} width="820" closeModal={() => setIsModalEditOpen(false)} onSubmitData={() => setApproved(true)} setIsModalOpen={setIsModalEditOpen} modalType={eFormType.Edit}>
              <DailyInventoryForm
                products={products}
                tempItem={selectedVal}
                createOrUpdate={approved}
                modalType={eFormType.Edit}
                onSubmitData={() => {
                  setApproved(false);
                  setIsModalEditOpen(false);
                  getTable();
                }}
              />
            </EditModal>
          )}

          {isModalOpen && (
            <EditModal title={i18n.t('dailyInventory.actions.add', { defaultValue: 'Add Daily Inventory' })} width="820" closeModal={() => setIsModalOpen(false)} onSubmitData={() => setApproved(true)} setIsModalOpen={setIsModalOpen} modalType={eFormType.New}>
              <DailyInventoryForm
                products={products}
                createOrUpdate={approved}
                modalType={eFormType.New}
                onSubmitData={() => {
                  setApproved(false);
                  setIsModalOpen(false);
                  getTable();
                }}
              />
            </EditModal>
          )}
        </div>
      </div>
    </BasicCli>
  );
};

export default DailyInventoryGrid;
